import Accordion from "@components/C11_VerticalAccordion/Accordion";
import Cta from "@components/Cta/Cta";
import { NextContentfulImage } from "@components/Image";
import type { Document } from "@contentful/rich-text-types";
import { Image as ImageProps } from "@customTypes/image";
import { Link, Link as LinkType } from "@customTypes/link";
import { richTextifyEnhanced } from "@utilities/richtext";
import clsx from "clsx";
import styles from "./C25_NavigationManual.module.scss";

export interface Fields {
  title: string;
  titleEnhanced: Document;
  titleEnhancedStyle?: string;
  titleUrl?: LinkType;
  description: string;
  learnMoreLink: Link;
  learnMoreLinkStyle: string;
  accordionTitle: string;
  expandLinks: boolean;
  flip: boolean;
  accordionOpen: boolean;
  programmeBlockLinks: Link[];
  image: ImageProps;
  isActive?: boolean;
  isBlue?: boolean;
}

interface ProgrammeBlockProps extends Fields {
  fullWidth?: boolean;
}

export const ProgrammeBlock = (props: ProgrammeBlockProps) => {
  const {
    title,
    titleUrl,
    titleEnhanced,
    titleEnhancedStyle,
    description,
    learnMoreLink,
    learnMoreLinkStyle,
    accordionTitle,
    accordionOpen,
    expandLinks,
    flip,
    programmeBlockLinks,
    image,
    fullWidth = false,
    isActive = true,
    isBlue = false,
  } = props;

  const getHighlightStyle = (style: string) =>
    isBlue ? (style === "amber" ? "amber" : "") : style || "";

  const linkStyle = getHighlightStyle(learnMoreLinkStyle);
  const titleStyle = getHighlightStyle(titleEnhancedStyle);

  const getLinks = () =>
    expandLinks
      ? programmeBlockLinks.slice(0, 15)
      : programmeBlockLinks.slice(0, 20);

  const headingId =
    title
      ?.toLowerCase()
      .trim()
      .replace(/[\s/_]+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/^-+|-+$/g, "") || "item";

  const programmeTitle = (
    <h4 id={headingId} className={titleStyle || ""}>
      {titleEnhanced ? (
        richTextifyEnhanced(titleEnhanced)
      ) : titleUrl?.fields?.url ? (
        <a href={titleUrl?.fields?.url}>{titleUrl?.fields?.linkText}</a>
      ) : (
        title
      )}
    </h4>
  );

  const programmeBlock = (
    <>
      {programmeTitle}
      <p>{description}</p>
      {learnMoreLink && (
        <Cta
          {...learnMoreLink.fields}
          type="tertiary"
          icon="icon-arrow"
          customClass={styles[linkStyle]}
          ariaDescribedBy={headingId}
        />
      )}
      {programmeBlockLinks?.length > 0 && (
        <Accordion
          accordionItems={[
            {
              fields: {
                title: (
                  <>
                    <span className="chevron"></span>
                    <span className="accordion-title">{accordionTitle}</span>
                  </>
                ),
                // @ts-expect-error type mismatch
                content: getLinks().map((link) => ({
                  ...link.fields,
                })),
              },
            },
          ]}
          singleDisplay={false}
          defaultOpen={accordionOpen}
          title={accordionTitle}
          contextLabelId={headingId}
          customClass={styles[linkStyle]}
        />
      )}
    </>
  );

  return (
    <div
      className={clsx(
        styles["programme-block"],
        programmeBlockLinks?.length > 0 && styles["accordion-block"],
        !image && styles["no-img"],
        flip && styles["flip"]
      )}
      aria-hidden={!isActive}
      // @ts-expect-error inter is not recognized by TS
      inert={!isActive ? "true" : undefined}
    >
      {image && (
        <div className={styles["image-wrapper"]}>
          <div className={styles["inner-wrap"]}>
            <NextContentfulImage
              src={image.fields.file.url}
              alt={image.fields.description || ""}
              width={590}
              height={590}
              sizes="(max-width: 1024px) 100vw, 590px"
              quality={90}
            />
          </div>
        </div>
      )}
      <div className={styles["programme-info"]}>
        {image && fullWidth ? (
          <div className={styles["inner-wrapper"]}>{programmeBlock}</div>
        ) : (
          programmeBlock
        )}
      </div>
    </div>
  );
};

export default ProgrammeBlock;
